import React, { useState } from 'react';
import { useAuth } from './context/AuthContext';

// Self-service profile page. Credentials live in Supabase, so changing the
// password goes through the Supabase client (updateUser); deleting the account
// removes the local profile and the Supabase identity via /api/auth/account.
const ProfilePage = () => {
  const { user, role, updatePassword, deleteAccount, logout } = useAuth();

  // --- Change password state ----------------------------------------------
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (!newPassword) {
      setPwError('Please enter a new password.');
      return;
    }
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New password and confirmation do not match.');
      return;
    }
    setPwSubmitting(true);
    const res = await updatePassword({ password: newPassword });
    setPwSubmitting(false);
    if (res.ok) {
      setPwSuccess('Password updated successfully.');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPwError(res.error || 'Could not change password.');
    }
  };

  // --- Delete account state -----------------------------------------------
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const isSuperuser = role === 'superuser';

  const onDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }
    setDeleteSubmitting(true);
    const res = await deleteAccount();
    setDeleteSubmitting(false);
    if (res.ok) {
      // deleteAccount already signs out of Supabase; ensure local state clears.
      logout();
    } else {
      setDeleteError(res.error || 'Could not delete account.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-4">
      <h1 className="text-2xl font-bold text-ink mb-1">Profile</h1>
      <p className="text-sm text-muted mb-6">Manage your account settings.</p>

      {/* Account info card */}
      <section className="bg-surface rounded-lg shadow-sm border border-hairline p-4 sm:p-6 mb-6">
        <h2 className="font-semibold text-ink mb-3">Account</h2>
        <dl className="text-sm grid grid-cols-3 gap-y-2">
          <dt className="text-muted">Username</dt>
          <dd className="col-span-2 text-ink">{user?.username || '—'}</dd>
          <dt className="text-muted">Email</dt>
          <dd className="col-span-2 text-ink">{user?.email || '—'}</dd>
          <dt className="text-muted">Role</dt>
          <dd className="col-span-2">
            <span className="px-2 py-0.5 rounded bg-surface-2 text-ink text-xs">{role}</span>
          </dd>
        </dl>
      </section>

      {/* Change password card */}
      <section className="bg-surface rounded-lg shadow-sm border border-hairline p-4 sm:p-6 mb-6">
        <h2 className="font-semibold text-ink mb-3">Change password</h2>
        <form onSubmit={onChangePassword} className="space-y-3">
          <div>
            <label className="block text-sm text-muted mb-1">New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full bg-surface border border-hairline text-ink rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
            />
            <p className="text-xs text-muted mt-1">At least 8 characters.</p>
          </div>
          <div>
            <label className="block text-sm text-muted mb-1">Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full bg-surface border border-hairline text-ink rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
            />
          </div>

          {pwError && <p className="text-sm text-danger">{pwError}</p>}
          {pwSuccess && <p className="text-sm text-positive">{pwSuccess}</p>}

          <button
            type="submit"
            disabled={pwSubmitting}
            className="px-4 py-2 bg-brand text-white text-sm rounded hover:bg-brand-hover disabled:opacity-60"
          >
            {pwSubmitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </section>

      {/* Danger zone */}
      <section className="bg-surface rounded-lg shadow-sm border border-danger/30 p-4 sm:p-6">
        <h2 className="font-semibold text-danger mb-1">Danger zone</h2>
        <p className="text-sm text-muted mb-3">
          Permanently delete your account. This action cannot be undone.
        </p>

        {isSuperuser ? (
          <p className="text-sm text-warn bg-warn/10 border border-warn/30 rounded p-3">
            Superuser accounts must be removed by another superuser from the
            Users admin page.
          </p>
        ) : !deleteOpen ? (
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="px-4 py-2 bg-brand text-white text-sm rounded hover:bg-brand-hover"
          >
            Delete my account…
          </button>
        ) : (
          <form onSubmit={onDeleteAccount} className="space-y-3">
            <div>
              <label className="block text-sm text-muted mb-1">
                Type <span className="font-mono font-semibold">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full bg-surface border border-hairline text-ink rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-danger/40 focus:border-danger"
              />
            </div>

            {deleteError && <p className="text-sm text-danger">{deleteError}</p>}

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={deleteSubmitting}
                className="px-4 py-2 bg-brand text-white text-sm rounded hover:bg-brand-hover disabled:opacity-60"
              >
                {deleteSubmitting ? 'Deleting…' : 'Permanently delete account'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeleteConfirmText('');
                  setDeleteError('');
                }}
                className="px-4 py-2 bg-surface-2 text-ink text-sm rounded hover:bg-hairline"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default ProfilePage;
